import { httpService } from "../../../base/services/httpService.service";
import { LocalStorageService } from "../../../base/services/local-storage.service";
import { SessionService } from "../../../base/services/session.service";
import AuthService, { SIGN_UP_TOKEN_KEY } from "../../../base/services/authentication.service";

// Everything a registration in progress keeps between renders, reloads and
// steps. The flow spans three steps, two routes and a modal, and every one of
// them reads the same account, the same plan and the same billing period, so
// what is stored and what it means is stated here once instead of being spelled
// out again at each call site.
//
// It lives in the session, which is the tab: a registration is abandoned by
// closing the tab it was started in, and the account behind it is picked up
// again by signing in — the login page mails a fresh code to an address that
// was never confirmed, and sends an account that never subscribed back to the
// package step — so nothing here has to outlive its tab.

/** Everything a registration in progress leaves in the session. */
export const REGISTRATION_SESSION_KEYS = [
   "registeringAccount",
   "awaitingEmailConfirmation",
   "isAccountConfirmed",
   "isOnSecondStep",
   "currentOrgId",
   "currentUserId",
   "registeringPlan",
   "registeringSubscription",
   "registeringPaymentType",
   "registeredOrganizationLevel",
   "addPaymentDetails",
   "subscriptionId",
   SIGN_UP_TOKEN_KEY
];

/**
 * Let go of the registration in this tab. Named key by key rather than cleared
 * wholesale, because the session is not only ours.
 */
export const clearRegistration = () =>
   REGISTRATION_SESSION_KEYS.forEach((key) => SessionService.removeItem(key));

/* -------------------------------------------------------------------------- */
/* Where the registration has got to                                          */
/* -------------------------------------------------------------------------- */

/**
 * The account exists and the address behind it is proven, which is what the
 * review step waits on. The organization id is part of the question: it is
 * what a subscription is created against, and a step that cannot name the
 * organization it is subscribing has nothing to offer.
 *
 * Confirming is what this asks about, not registering: `registeringAccount` is
 * written the moment /Account/register answers, while the code it mails out is
 * still unanswered, so it says nothing about the address behind the account.
 */
export const hasConfirmedAccount = () =>
   Boolean(
      (SessionService.getItem("isAccountConfirmed") || SessionService.getItem("isOnSecondStep")) &&
         SessionService.getItem("currentOrgId")
   );

/**
 * The account exists and the code mailed out for it never came back. Kept in
 * the session so a reload lands back on the modal it was left in rather than on
 * the review, which the address is still unproven for.
 */
export const isAwaitingConfirmation = () =>
   Boolean(SessionService.getItem("awaitingEmailConfirmation"));

/** The code has gone out and nothing has come back yet. */
export const startAwaitingConfirmation = () =>
   SessionService.setItem("awaitingEmailConfirmation", true);

/** The address is proven. Nothing is waiting on a code any more. */
export const finishAwaitingConfirmation = () => {
   SessionService.setItem("isAccountConfirmed", true);
   SessionService.removeItem("awaitingEmailConfirmation");
};

/* -------------------------------------------------------------------------- */
/* The billing flow                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Open the sign up's billing session, from the token the new billing flow
 * (Billing:NewFlow) answers confirming the email with — or signing in to an
 * account whose sign up never picked a plan. With the flow off there is no
 * token, and this is not called.
 *
 * That token can't be used for billing. It only says who the person is: every
 * /Payment endpoint refuses it with a 401. Called with it, selectOrganization
 * answers with a token for the organization, the one the billing calls that
 * finish a sign up are made with, and that is what is kept — in the tab's
 * session, not as a sign in (AuthService.getSignUpToken).
 *
 * Resolves true once it is kept, and false when selectOrganization gave none,
 * in which case nothing is kept: this sign up then has no way to finish on the
 * new flow, and must not be taken for one on the old flow either.
 */
export const openSignUpSession = async (identityToken, { userId, organizationId }) => {
   if (!identityToken || !userId || !organizationId) return false;

   try {
      const params = new URLSearchParams({ userId, organizationId });
      const response = await httpService.get(
         `/Account/selectOrganization?${params.toString()}`,
         undefined,
         undefined,
         {
            headers: {
               Authorization: `Bearer ${identityToken}`,
               "X-OrganizationId": organizationId
            }
         }
      );
      const token = response?.data?.token;
      if (!token) return false;

      AuthService.setSignUpToken(token);
      return true;
   } catch {
      return false;
   }
};

/* -------------------------------------------------------------------------- */
/* The profile                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The profile fields the sign up form owns, and so the only ones it is filled
 * back in from. The password is deliberately not among them — see below.
 */
const PROFILE_FORM_FIELDS = ["email", "fullname", "phone"];

/**
 * Who the account that was just created belongs to. The organization id is what
 * a subscription is created against and what a card is filed under; the user id
 * is what the confirmation code is checked against.
 */
export const saveRegisteredIdentity = ({ organizationId, userId }) => {
   SessionService.setItem("currentOrgId", organizationId);
   SessionService.setItem("currentUserId", userId);
};

export const getRegisteringAccount = () => SessionService.getItem("registeringAccount");

/**
 * What was registered, kept for the review step and for filling the form back
 * in after a reload.
 *
 * The three the form asks for and keeps. The organization's name, type and
 * country are filled in from these rather than typed (registrationDefaults.js),
 * and an account resumed from the login page arrives carrying its own — which
 * is why the review step reads whatever is there instead of expecting all of
 * it.
 *
 * Not the password: once /Account/register has answered, the form is locked to
 * the account it created and is never submitted again, so keeping the password
 * would be storing a credential the flow has no further use for.
 */
export const saveRegisteringAccount = (values) =>
   SessionService.setItem("registeringAccount", {
      fullname: values.fullname,
      phone: values.phone,
      email: values.email
   });

/**
 * The stored profile as the form takes it back.
 *
 * A registration resumed from the login page names its country and its
 * organization type — `{ name }` — where a fresh one carries their ids, because
 * the login response reports them that way. The review step reads both; the
 * form's selects are keyed on ids alone, so a name-shaped value is left out
 * rather than handed over as the object it is.
 */
export const getProfileFormValues = () => {
   const account = getRegisteringAccount();
   if (!account) return undefined;

   return Object.fromEntries(
      PROFILE_FORM_FIELDS.filter(
         (field) => account[field] !== undefined && typeof account[field] !== "object"
      ).map((field) => [field, account[field]])
   );
};

/* -------------------------------------------------------------------------- */
/* The plan                                                                   */
/* -------------------------------------------------------------------------- */

export const getRegisteringPlan = () => SessionService.getItem("registeringPlan");

export const saveRegisteringPlan = (plan) => SessionService.setItem("registeringPlan", plan);

/**
 * Which of the two prices on a packet the registration is buying. It outlives
 * the package step — the review step and the subscription call both quote it —
 * and the subscription screens signed-in accounts use read the same key, so it
 * is kept where they keep it.
 *
 * Annual unless something has said otherwise, which is what the package step
 * opens on: read as `=== true` instead, a key nobody has written yet would
 * quote monthly prices under a toggle sitting on annual.
 */
export const isBilledYearly = () => LocalStorageService.getItem("isYearly") !== false;

export const setBilledYearly = (isYearly) => LocalStorageService.setItem("isYearly", isYearly);

